// TanStack Query hooks for the Report Templates module. Report templates are
// named, versioned layouts that drive document generation (DARs, incidents,
// invoices, post orders, surveys, advances, CoC). A template owns an ordered
// list of versions; exactly one may be 'active' (the one document generation
// uses). Mirrors packages/api/src/routes/report-templates.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateReportTemplateInput,
  CreateReportTemplateVersionInput,
  PublishReportTemplateInput,
  ReportTemplate,
  ReportTemplateKind,
  ReportTemplateResponse,
  ReportTemplatesListResponse,
  ReportTemplateVersion,
  ReportTemplateVersionResponse,
  ReportTemplateVersionsListResponse,
  ReportTemplateVersionStatus,
  UpdateReportTemplateInput,
  UpdateReportTemplateVersionInput,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------- Query keys ----------

const KEY = 'report-templates';

export const templateKeys = {
  all: [KEY] as const,
  lists: () => [KEY, 'list'] as const,
  list: (filters: ReportTemplateFilters) => [KEY, 'list', { ...filters }] as const,
  detail: (id: number) => [KEY, 'detail', id] as const,
  versions: (templateId: number, filters: VersionFilters = {}) =>
    [KEY, 'detail', templateId, 'versions', { ...filters }] as const,
  version: (templateId: number, versionId: number) =>
    [KEY, 'detail', templateId, 'versions', versionId] as const,
};

// =============================================================================
// Templates
// =============================================================================

export interface ReportTemplateFilters {
  kind?: ReportTemplateKind;
}

export function useReportTemplates(filters: ReportTemplateFilters = {}) {
  const params = new URLSearchParams();
  if (filters.kind) params.set('kind', filters.kind);
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/report-templates?${qs}` : '/api/report-templates';
  return useQuery({
    queryKey: templateKeys.list(filters),
    queryFn: ({ signal }) => api.get<ReportTemplatesListResponse>(url, signal),
  });
}

export function useReportTemplate(id: number | null) {
  return useQuery({
    queryKey: templateKeys.detail(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<ReportTemplateResponse>(`/api/report-templates/${id}`, signal),
  });
}

export function useCreateReportTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReportTemplateInput) =>
      api.post<ReportTemplateResponse>('/api/report-templates', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

export interface UpdateReportTemplateArgs extends UpdateReportTemplateInput {
  id: number;
}

export function useUpdateReportTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateReportTemplateArgs) =>
      api.patch<ReportTemplateResponse>(`/api/report-templates/${id}`, patch),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: templateKeys.lists() });
      void qc.invalidateQueries({ queryKey: templateKeys.detail(id) });
    },
  });
}

export function useDeleteReportTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/report-templates/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

/**
 * Publish (promote) a version to 'active' for its template. The previously
 * active version is archived server-side; the template's active_version_id is
 * repointed. Gated on report_template.publish.
 */
export interface PublishReportTemplateArgs extends PublishReportTemplateInput {
  templateId: number;
}

export function usePublishReportTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, version_id }: PublishReportTemplateArgs) =>
      api.post<ReportTemplateResponse>(`/api/report-templates/${templateId}/publish`, {
        version_id,
      }),
    onSuccess: (_data, { templateId }) => {
      // Publishing touches both the template (active_version_id) and the
      // version statuses, so refresh the whole subtree.
      void qc.invalidateQueries({ queryKey: templateKeys.lists() });
      void qc.invalidateQueries({ queryKey: templateKeys.detail(templateId) });
    },
  });
}

// =============================================================================
// Versions
// =============================================================================

export interface VersionFilters {
  status?: ReportTemplateVersionStatus;
}

export function useReportTemplateVersions(
  templateId: number | null,
  filters: VersionFilters = {},
) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString();
  const base = `/api/report-templates/${templateId}/versions`;
  const url = qs.length > 0 ? `${base}?${qs}` : base;
  return useQuery({
    queryKey: templateKeys.versions(templateId ?? 0, filters),
    enabled: templateId !== null && templateId > 0,
    queryFn: ({ signal }) =>
      api.get<ReportTemplateVersionsListResponse>(url, signal),
  });
}

export function useReportTemplateVersion(
  templateId: number | null,
  versionId: number | null,
) {
  return useQuery({
    queryKey: templateKeys.version(templateId ?? 0, versionId ?? 0),
    enabled:
      templateId !== null && templateId > 0 && versionId !== null && versionId > 0,
    queryFn: ({ signal }) =>
      api.get<ReportTemplateVersionResponse>(
        `/api/report-templates/${templateId}/versions/${versionId}`,
        signal,
      ),
  });
}

export interface CreateVersionArgs extends CreateReportTemplateVersionInput {
  templateId: number;
}

export function useCreateReportTemplateVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, ...input }: CreateVersionArgs) =>
      api.post<ReportTemplateVersionResponse>(
        `/api/report-templates/${templateId}/versions`,
        input,
      ),
    onSuccess: (_data, { templateId }) => {
      void qc.invalidateQueries({ queryKey: templateKeys.detail(templateId) });
    },
  });
}

export interface UpdateVersionArgs extends UpdateReportTemplateVersionInput {
  templateId: number;
  versionId: number;
}

export function useUpdateReportTemplateVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, versionId, ...patch }: UpdateVersionArgs) =>
      api.patch<ReportTemplateVersionResponse>(
        `/api/report-templates/${templateId}/versions/${versionId}`,
        patch,
      ),
    onSuccess: (_data, { templateId }) => {
      void qc.invalidateQueries({ queryKey: templateKeys.detail(templateId) });
    },
  });
}

export interface DeleteVersionArgs {
  templateId: number;
  versionId: number;
}

export function useDeleteReportTemplateVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, versionId }: DeleteVersionArgs) =>
      api.delete<void>(`/api/report-templates/${templateId}/versions/${versionId}`),
    onSuccess: (_data, { templateId }) => {
      // Deleting the active version unpublishes the template, so invalidate
      // the template detail (active_version_id) as well as the version list.
      void qc.invalidateQueries({ queryKey: templateKeys.lists() });
      void qc.invalidateQueries({ queryKey: templateKeys.detail(templateId) });
    },
  });
}

// Re-export the wire types other modules commonly need alongside the hooks.
export type {
  ReportTemplate,
  ReportTemplateKind,
  ReportTemplateVersion,
  ReportTemplateVersionStatus,
};
